import axios from "axios";
import { Package, Eye, Calendar, CheckCircle, XCircle, Clock, ChevronDown, RotateCcw, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { showError } from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";

const ITEMS_PER_PAGE = 10;

const RequestStats = () => {
  const { admin } = useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    verified: 0,
  });

  const totalPages = Math.ceil(requests.length / ITEMS_PER_PAGE);
  const paginatedRequests = requests.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (admin?.id) {
      fetchRequests();
    }
  }, [admin?.id]);

  const fetchRequests = async () => {
    if (!admin?.id) {
      showError("Staff information not found");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-requests/staff/${admin.id}`
      );

      const data = res?.data?.data || [];
      setRequests(data);

      const pending = data.filter(r => r.status === "pending").length;
      const verified = data.filter(r => r.status === "verified").length;

      setStats({
        total: data.length,
        pending,
        verified,
      });
    } catch (err) {
      console.error("Fetch requests failed", err);
      showError("Failed to load requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturnProduct = async (requestId, productId, returnedQuantity, damagedQuantity, returnRemark) => {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/product-requests/${requestId}/return-products`,
        {
          products: [
            {
              productId,
              returnedQuantity,
              damagedQuantity,
              returnRemark: returnRemark || "",
            },
          ],
        }
      );

      fetchRequests();
      setSelectedRequest(res.data.data);
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to process return");
    }
  };

  const viewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" },
      approved: { bg: "bg-green-100", text: "text-green-800", label: "Approved" },
      rejected: { bg: "bg-red-100", text: "text-red-800", label: "Rejected" },
      verified: { bg: "bg-blue-100", text: "text-blue-800", label: "Verified" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 no-scrollbar">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor/10 to-accentTextColor/10 backdrop-blur-md border-b border-primaryColor/20 shadow-sm">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="leading-tight">
              <h1 className="text-base md:text-xl font-bold text-primaryTextColor">
                Request Stats
              </h1>
              <p className="text-xs md:text-sm text-secondaryTextColor mt-0.5">
                Track your product requests
              </p>
            </div>

            <div className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white shadow-md border border-gray-200 text-secondaryTextColor text-xs md:text-sm">
              <Package size={14} className="md:w-4 md:h-4" />
              <span className="font-medium">{stats.total}</span>
              <span className="hidden sm:inline">Request{stats.total !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 sm:py-6 md:py-8 pb-20">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
          {isLoading ? (
            <>
              <StatCardSkeleton />
              <StatCardSkeleton />
              <StatCardSkeleton />
            </>
          ) : (
            <>
              <div className="bg-white rounded-lg md:rounded-md shadow-md p-3 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Total Requests</p>
                    <p className="text-2xl md:text-3xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Package className="text-blue-600" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg md:rounded-md shadow-md p-3 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Pending</p>
                    <p className="text-2xl md:text-3xl font-bold text-yellow-600">{stats.pending}</p>
                  </div>
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Clock className="text-yellow-600" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg md:rounded-md shadow-md p-3 md:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Verified</p>
                    <p className="text-2xl md:text-3xl font-bold text-green-600">{stats.verified}</p>
                  </div>
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-green-600" size={20} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-lg md:rounded-md shadow-md p-3 md:p-6">
          <h2 className="text-base md:text-lg font-bold text-gray-800 mb-3 md:mb-4">My Product Requests</h2>

          {isLoading ? (
            <RequestTableSkeleton />
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <Package size={48} className="md:w-16 md:h-16 mx-auto text-gray-300 mb-3 md:mb-4" />
              <p className="text-sm md:text-base text-gray-500">No requests found</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-max border border-gray-200 text-sm">
                  <thead className="bg-gray-100">
                    <tr className="text-gray-700">
                      <th className="px-4 py-3 text-left font-semibold">Request Date</th>
                      <th className="px-4 py-3 text-left font-semibold">Branch</th>
                      <th className="px-4 py-3 text-center font-semibold">Products</th>
                      <th className="px-4 py-3 text-center font-semibold">Status</th>
                      <th className="px-4 py-3 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {paginatedRequests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {new Date(request.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-4 py-3">
                          {request.branch?.branchName || "N/A"}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-semibold">
                            {request.products?.length || 0}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(request.status)}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => viewDetails(request)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primaryBtnColor text-white rounded-lg hover:bg-primaryBtnHoverColor transition"
                          >
                            <Eye size={16} />
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {paginatedRequests.map((request) => (
                  <div
                    key={request._id}
                    onClick={() => viewDetails(request)}
                    className="bg-white border rounded-lg p-3 active:bg-gray-50 transition"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">
                          {new Date(request.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                        <p className="font-semibold text-sm text-gray-800 mt-0.5">
                          {request.branch?.branchName || "N/A"}
                        </p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Package size={14} />
                        <span>{request.products?.length || 0} Product{request.products?.length !== 1 ? 's' : ''}</span>
                      </div>
                      <ChevronDown size={16} className="text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                  <p className="text-xs md:text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </p>

                  <div className="flex gap-2">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                      className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      Prev
                    </button>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                      className="px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetails && selectedRequest && (
          <>
            {/* Desktop Modal */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="hidden md:flex fixed inset-0 bg-black/50 z-50 items-center justify-center p-4"
              onClick={() => setShowDetails(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
              >
                <div className="bg-accentTextColor/70 text-white px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold">Request Details</h2>
                  <button onClick={() => setShowDetails(false)} className="hover:scale-110 transition">
                    <XCircle size={24} />
                  </button>
                </div>

                <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                  <RequestDetailsContent
                    request={selectedRequest}
                    onReturn={handleReturnProduct}
                    getStatusBadge={getStatusBadge}
                  />
                </div>

                <div className="border-t p-6">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="w-full px-4 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>

            {/* Mobile Bottom Sheet */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowDetails(false)}
            >
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
              >
                <div className="flex justify-center py-2 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor">
                  <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
                </div>

                <div className="px-4 py-3 border-b flex bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor items-center justify-between">
                  <h2 className="text-lg font-bold text-white">Request Details</h2>
                  <button onClick={() => setShowDetails(false)} className="p-2 text-white hover:bg-gray-100 rounded-full transition">
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <RequestDetailsContent
                    request={selectedRequest}
                    onReturn={handleReturnProduct}
                    getStatusBadge={getStatusBadge}
                    isMobile={true}
                  />
                </div>

                <div className="border-t p-4">
                  <button
                    onClick={() => setShowDetails(false)}
                    className="w-full px-4 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg active:bg-gray-300 transition"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Return Quantity Form ────────────────────────────────────────────────────

const ReturnQuantityForm = ({ item, requestId, onReturn, onCancel }) => {
  const assignedQty = item.assignedQuantity || 0;

  const [returnedQty, setReturnedQty] = useState(assignedQty);
  const [damagedQty, setDamagedQty] = useState(0);
  const [returnRemark, setReturnRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const total = Number(returnedQty) + Number(damagedQty);
  const isValid = total === assignedQty && returnedQty >= 0 && damagedQty >= 0;

  const handleReturnedChange = (val) => {
    const parsed = Math.max(0, Math.min(assignedQty, Number(val)));
    setReturnedQty(parsed);
    setDamagedQty(assignedQty - parsed);
    setError("");
  };

  const handleDamagedChange = (val) => {
    const parsed = Math.max(0, Math.min(assignedQty, Number(val)));
    setDamagedQty(parsed);
    setReturnedQty(assignedQty - parsed);
    setError("");
  };

  const handleSubmit = async () => {
    if (!isValid) {
      setError(`Returned + Damaged must equal assigned quantity (${assignedQty})`);
      return;
    }
    if (damagedQty > 0 && !returnRemark.trim()) {
      setError("Please add a remark for damaged items");
      return;
    }

    setSubmitting(true);
    try {
      await onReturn(requestId, item.product._id, Number(returnedQty), Number(damagedQty), returnRemark);
      onCancel();
    } catch (e) {
      setError("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-3 p-3 md:p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
      <p className="text-xs md:text-sm font-semibold text-blue-800 flex items-center gap-1.5">
        <RotateCcw size={14} />
        Return / Damage — Assigned Qty: <span className="font-bold">{assignedQty}</span>
      </p>

      <div className="grid grid-cols-2 gap-3">
        {/* Returned Qty */}
        <div>
          <label className="block text-xs font-medium text-green-700 mb-1">
            ✔ Returned
          </label>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleReturnedChange(returnedQty - 1)}
              disabled={returnedQty <= 0}
              className="w-7 h-7 flex items-center justify-center rounded bg-green-100 hover:bg-green-200 text-green-700 font-bold disabled:opacity-40 transition text-sm"
            >
              −
            </button>
            <input
              type="number"
              min={0}
              max={assignedQty}
              value={returnedQty}
              onChange={(e) => handleReturnedChange(e.target.value)}
              className="w-14 text-center border border-green-300 rounded px-1 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-green-400"
            />
            <button
              onClick={() => handleReturnedChange(returnedQty + 1)}
              disabled={returnedQty >= assignedQty}
              className="w-7 h-7 flex items-center justify-center rounded bg-green-100 hover:bg-green-200 text-green-700 font-bold disabled:opacity-40 transition text-sm"
            >
              +
            </button>
          </div>
        </div>

        {/* Damaged Qty */}
        <div>
          <label className="block text-xs font-medium text-red-700 mb-1">
            ✖ Damaged
          </label>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleDamagedChange(damagedQty - 1)}
              disabled={damagedQty <= 0}
              className="w-7 h-7 flex items-center justify-center rounded bg-red-100 hover:bg-red-200 text-red-700 font-bold disabled:opacity-40 transition text-sm"
            >
              −
            </button>
            <input
              type="number"
              min={0}
              max={assignedQty}
              value={damagedQty}
              onChange={(e) => handleDamagedChange(e.target.value)}
              className="w-14 text-center border border-red-300 rounded px-1 py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-red-400"
            />
            <button
              onClick={() => handleDamagedChange(damagedQty + 1)}
              disabled={damagedQty >= assignedQty}
              className="w-7 h-7 flex items-center justify-center rounded bg-red-100 hover:bg-red-200 text-red-700 font-bold disabled:opacity-40 transition text-sm"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Qty Summary */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-medium ${total === assignedQty ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        }`}>
        {total === assignedQty ? (
          <CheckCircle size={13} />
        ) : (
          <AlertTriangle size={13} />
        )}
        Total: {total} / {assignedQty}
        {total !== assignedQty && ` — difference: ${assignedQty - total}`}
      </div>

      {/* Remark */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Remark {damagedQty > 0 && <span className="text-red-500">*</span>}
        </label>
        <textarea
          value={returnRemark}
          onChange={(e) => setReturnRemark(e.target.value)}
          placeholder={damagedQty > 0 ? "Describe the damage..." : "Optional remarks..."}
          rows={2}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle size={12} /> {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={submitting || !isValid}
          className="flex-1 px-3 py-2 bg-primaryBtnColor hover:bg-primaryBtnHoverColor disabled:opacity-50 text-white text-xs font-semibold rounded transition"
        >
          {submitting ? "Submitting..." : "Submit Return"}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold rounded transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Request Details Content ─────────────────────────────────────────────────

const RequestDetailsContent = ({ request, onReturn, getStatusBadge, isMobile = false }) => {
  const [activeReturnId, setActiveReturnId] = useState(null);

  return (
    <>
      {/* Request Info */}
      <div className="mb-4 md:mb-6 grid grid-cols-2 gap-3 md:gap-4">
        <div>
          <p className="text-xs md:text-sm text-gray-600 mb-1">Request Date</p>
          <p className="font-semibold text-sm md:text-base">
            {new Date(request.createdAt).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
        <div>
          <p className="text-xs md:text-sm text-gray-600 mb-1">Branch</p>
          <p className="font-semibold text-sm md:text-base">{request.branch?.branchName || "N/A"}</p>
        </div>
        <div>
          <p className="text-xs md:text-sm text-gray-600 mb-1">Overall Status</p>
          <div>{getStatusBadge(request.status)}</div>
        </div>
        <div>
          <p className="text-xs md:text-sm text-gray-600 mb-1">Total Products</p>
          <p className="font-semibold text-sm md:text-base">{request.products?.length || 0}</p>
        </div>
        {request.status === "verified" && (
          <div className="col-span-2 px-3 md:px-4 py-2 bg-green-50 text-green-700 text-xs md:text-sm rounded">
            ✔ This request is verified. Reusable products must be returned after use.
          </div>
        )}
      </div>

      {/* Products List */}
      <div>
        <h3 className="font-bold text-sm md:text-base text-gray-800 mb-2 md:mb-3">Requested Products</h3>
        <div className="space-y-2 md:space-y-3">
          {request.products?.map((item, index) => {
            const isReturnOpen = activeReturnId === item.product?._id;

            return (
              <div
                key={index}
                className="border rounded-lg p-3 md:p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-start gap-2 md:gap-4">
                  {/* Product Image */}
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded flex-shrink-0">
                    {item.product?.productImg ? (
                      <img
                        src={item.product.productImg}
                        alt={item.product?.productName}
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={isMobile ? 20 : 24} className="text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0 capitalize">
                    <h4 className="font-semibold text-sm md:text-base text-gray-800 mb-1 truncate">
                      {item.product?.productName || "N/A"}
                    </h4>
                    <p className="text-xs md:text-sm text-gray-500 mb-2">
                      {item.product?.brand || "N/A"}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-2">
                      {/* Usage Type */}
                      <span
                        className={`px-2 py-0.5 md:py-1 rounded text-[10px] md:text-xs font-medium ${item.product?.productUsageType === "reusable"
                          ? "bg-green-100 text-green-700"
                          : "bg-orange-100 text-orange-700"
                          }`}
                      >
                        {item.product?.productUsageType === "reusable" ? "Reusable" : "Use & Throw"}
                      </span>

                      {/* Return Status Badge */}
                      {item.status === "approved" && (
                        <span
                          className={`px-2 py-0.5 md:py-1 rounded text-[10px] md:text-xs font-medium ${item.returnStatus === "returned"
                            ? "bg-green-100 text-green-700"
                            : item.returnStatus === "damaged"
                              ? "bg-red-100 text-red-700"
                              : item.returnStatus === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                        >
                          {item.returnStatus === "not_required"
                            ? "Return Not Required"
                            : item.returnStatus === "damaged"
                              ? "Damaged"
                              : item.returnStatus === "returned"
                                ? "Returned"
                                : `Return: ${item.returnStatus || "Pending"}`}
                        </span>
                      )}
                    </div>

                    {/* RETURN ACTION TRIGGER */}
                    {request.status === "verified" &&
                      item.product?.productUsageType === "reusable" &&
                      item.status === "approved" &&
                      item.returnStatus === "pending" && (
                        <>
                          {!isReturnOpen ? (
                            <button
                              onClick={() => setActiveReturnId(item.product._id)}
                              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white text-xs font-semibold rounded transition"
                            >
                              <RotateCcw size={12} />
                              Return / Mark Damaged
                            </button>
                          ) : (
                            <ReturnQuantityForm
                              item={item}
                              requestId={request._id}
                              onReturn={onReturn}
                              onCancel={() => setActiveReturnId(null)}
                            />
                          )}
                        </>
                      )}

                    {/* Qty Summary */}
                    <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm mt-2">
                      <div>
                        <span className="text-gray-600">Requested: </span>
                        <span className="font-semibold text-purple-600">{item.requestedQuantity}</span>
                      </div>

                      {item.status !== "pending" && (
                        <div>
                          <span className="text-gray-600">Assigned: </span>
                          <span className="font-semibold text-green-600">{item.assignedQuantity || 0}</span>
                        </div>
                      )}

                      {(item.returnStatus === "returned" || item.returnedQuantity > 0) && (
                        <div>
                          <span className="text-gray-600">Returned: </span>
                          <span className="font-semibold text-green-600">{item.returnedQuantity || 0}</span>
                        </div>
                      )}

                      {item.damagedQuantity > 0 && (
                        <div>
                          <span className="text-gray-600">Damaged: </span>
                          <span className="font-semibold text-red-600">{item.damagedQuantity}</span>
                        </div>
                      )}

                      <div>{getStatusBadge(item.status)}</div>
                    </div>

                    {/* Remark */}
                    {item.remark && (
                      <div className="mt-2 p-2 bg-gray-100 rounded text-xs md:text-sm">
                        <span className="font-semibold text-gray-700">Remark: </span>
                        <span className="text-gray-600">{item.remark}</span>
                      </div>
                    )}

                    {item.returnRemark && (
                      <div className={`mt-2 p-2 rounded text-xs md:text-sm ${item.returnStatus === "damaged" ? "bg-red-50" : "bg-gray-50"
                        }`}>
                        <span className={`font-semibold ${item.returnStatus === "damaged" ? "text-red-700" : "text-gray-700"
                          }`}>
                          {item.returnStatus === "damaged" ? "Damage Remark: " : "Return Remark: "}
                        </span>
                        <span className={item.returnStatus === "damaged" ? "text-red-600" : "text-gray-600"}>
                          {item.returnRemark}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ─── Skeleton Loaders ────────────────────────────────────────────────────────

const StatCardSkeleton = () => (
  <div className="bg-white rounded-lg md:rounded-md shadow-md p-3 md:p-6 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="h-3 md:h-4 bg-gray-200 rounded w-24 mb-2"></div>
        <div className="h-6 md:h-8 bg-gray-200 rounded w-16"></div>
      </div>
      <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-200 rounded-full"></div>
    </div>
  </div>
);

const RequestTableSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="h-12 md:h-16 bg-gray-200 rounded-lg"></div>
      </div>
    ))}
  </div>
);

export default RequestStats;